/**
 * Phase 06.2 / DEC-030 #1 — service charges (tenant-scoped catalog) and
 * per-capture child rows.
 *
 * service_charges:
 *   - RLS-isolated via tenant_id
 *   - UNIQUE (tenant_id, code) — idempotent seed + dedupe
 *   - UoM CHECK allowlist (GAL|BBL|BAG|TON|EACH|LB|HR)
 *   - Soft-delete via is_active=false (no DELETE endpoint per SVCCHG-03)
 *
 * haul_capture_service_charges:
 *   - NO tenant_id column — isolation TRANSITIVE via FK to haul_captures
 *     (haul_capture_tanks precedent — hauls.ts:339-385)
 *   - SNAPSHOT semantics on unit_of_measure + rate_cents_at_capture (DEC-024)
 *     Immutable post-INSERT. API sets explicitly. OMITTED from any
 *     ON CONFLICT DO UPDATE SET clause.
 *   - UNIQUE (haul_capture_id, service_charge_id) — one row per service per capture
 *   - quantity CHECK > 0
 *
 * Wave 0 — schema only. Migration 0014 lands the DDL.
 */

import {
  pgTable,
  uuid,
  text,
  integer,
  boolean,
  timestamp,
  numeric,
  index,
  uniqueIndex,
  pgPolicy,
  check,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';

import { appUserRole } from './enums.js';
import { tenants } from './users.js';
import { haulCaptures } from './hauls.js';

export const serviceCharges = pgTable(
  'service_charges',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    code: text('code').notNull(),
    label: text('label').notNull(),
    unitOfMeasure: text('unit_of_measure').notNull(),
    defaultRateCents: integer('default_rate_cents').notNull().default(0),
    sortOrder: integer('sort_order').notNull().default(0),
    isActive: boolean('is_active').notNull().default(true),
    // OPEN-111 (migration 0158) — the screen the charge is entered on: GAUGING (default)
    // or DISPOSAL (known only at or after unloading). Each charge is on ONE screen.
    stage: text('stage').notNull().default('GAUGING'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex('service_charges_tenant_code_idx').on(t.tenantId, t.code),
    check('service_charges_stage_check', sql`${t.stage} IN ('GAUGING','DISPOSAL')`),
    check(
      'service_charges_uom_check',
      sql`${t.unitOfMeasure} IN ('GAL','BBL','BAG','TON','EACH','LB','HR')`,
    ),
    check('service_charges_default_rate_nonneg', sql`${t.defaultRateCents} >= 0`),
    pgPolicy('service_charges_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type ServiceCharge = typeof serviceCharges.$inferSelect;
export type NewServiceCharge = typeof serviceCharges.$inferInsert;

/**
 * Child rows — per-capture service charges with snapshot semantics.
 * NO tenant_id; isolation transitive via FK to haul_captures.
 */
export const haulCaptureServiceCharges = pgTable(
  'haul_capture_service_charges',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    haulCaptureId: uuid('haul_capture_id')
      .notNull()
      .references(() => haulCaptures.id, { onDelete: 'cascade' }),
    serviceChargeId: uuid('service_charge_id')
      .notNull()
      .references(() => serviceCharges.id, { onDelete: 'restrict' }),
    quantity: numeric('quantity', { precision: 10, scale: 3 }).notNull(),
    // Snapshot — DEC-024 / DEC-030 #1. Immutable post-INSERT.
    // API sets explicitly. OMITTED from any ON CONFLICT DO UPDATE SET.
    unitOfMeasure: text('unit_of_measure').notNull(),
    rateCentsAtCapture: integer('rate_cents_at_capture').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    // OPEN-158 (migration 0225) — the leg that wrote the row: CAPTURE (the capture itself) or
    // DISPOSAL (disposal-complete). NULL = written before 0225 (not knowable). A leg's rows are
    // replaced as a set through haul_capture_service_charges_clear_leg (the only delete path).
    leg: text('leg'),
  },
  (t) => [
    index('haul_capture_service_charges_capture_idx').on(t.haulCaptureId),
    uniqueIndex('haul_capture_service_charges_dedupe_idx').on(t.haulCaptureId, t.serviceChargeId),
    check('haul_capture_service_charges_qty_positive', sql`${t.quantity} > 0`),
    check(
      'haul_capture_service_charges_uom_check',
      sql`${t.unitOfMeasure} IN ('GAL','BBL','BAG','TON','EACH','LB','HR')`,
    ),
    check('haul_capture_service_charges_rate_nonneg', sql`${t.rateCentsAtCapture} >= 0`),
    check(
      'haul_capture_service_charges_leg_check',
      sql`${t.leg} IS NULL OR ${t.leg} IN ('CAPTURE', 'DISPOSAL')`,
    ),
    // No pgPolicy — isolation transitive via FK to haul_captures.
    // Same posture as haul_capture_tanks (hauls.ts:339-385).
  ],
);

export type HaulCaptureServiceCharge = typeof haulCaptureServiceCharges.$inferSelect;
export type NewHaulCaptureServiceCharge = typeof haulCaptureServiceCharges.$inferInsert;
